import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ClassesContext from '../../context/ClassesContext';
import FilterSelect from './FilterSelect';
import { createStudent, deleteStudent } from '../../utils/accountsApi';
import { getCurrentUserEmail } from '../../utils/currentUser';
import './Students.css';
import './Teachers.css';
import Footer from '../Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ns-dashboard-api.kabilah.com';
const MAX_STUDENT_ACCOUNTS = 167;

// lastLoginAt is a UTC ISO timestamp from Firebase, or null if the student
// has never signed in to the app. Always shown in Eastern Time (EST/EDT
// handled by the America/New_York zone), labeled "ET", regardless of the
// viewer's own time zone.
function formatLastLogin(lastLoginAt) {
  const d = new Date(lastLoginAt);
  if (!lastLoginAt || isNaN(d)) return null;
  return `${d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })} ET`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Epoch ms of the student's most recent login, or null if they've never
// signed in (or the timestamp is unusable) — the same rule the Last Login
// cell uses to show "Never logged in", so filter/sort always match the table.
function lastLoginMs(student) {
  if (!student.hasLoggedIn || !student.lastLoginAt) return null;
  const ms = new Date(student.lastLoginAt).getTime();
  return isNaN(ms) ? null : ms;
}

// Rolling windows (last N×24h from now), not calendar weeks/months.
// "More than 30 days ago" deliberately excludes never-logged-in students:
// those are two different follow-ups (lapsed vs. never started).
const LAST_LOGIN_FILTERS = [
  { value: '', label: 'Any time', test: () => true },
  { value: '7d', label: 'Past 7 days', test: (ms, now) => ms !== null && now - ms <= 7 * DAY_MS },
  { value: '30d', label: 'Past 30 days', test: (ms, now) => ms !== null && now - ms <= 30 * DAY_MS },
  { value: 'over30', label: 'More than 30 days ago', test: (ms, now) => ms !== null && now - ms > 30 * DAY_MS },
  { value: 'never', label: 'Never', test: (ms) => ms === null },
];

function CreateStudentRow({ onCancel, onCreated }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName && !trimmedEmail) { onCancel(); return; }
    if (!trimmedName) { setError('Name is required.'); return; }
    if (!trimmedEmail) { setError('Email is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await createStudent(trimmedName, trimmedEmail);
      onCreated(res.data);
    } catch (err) {
      const backendMessage = err.response?.data?.error;
      if (err.response?.status === 500 && backendMessage) {
        // Nothing was created server-side on this failure — safe to retry.
        setError(`${backendMessage}. No account was created — you can try again.`);
      } else {
        setError(backendMessage || 'Could not create the student account. Please try again.');
      }
      setSaving(false);
    }
  };

  return (
    <div className="student-row student-row-creating">
      <div className="teacher-create-fields">
        <input
          ref={inputRef}
          className="review-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          disabled={saving}
          required
        />
        <input
          type="email"
          className="review-name-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          disabled={saving}
          required
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel(); }}
        />
      </div>
      {error && <span className="review-field-error">{error}</span>}
      <div className="students-col-actions">
        <button className="title-button" onClick={commit} disabled={saving}>
          {saving ? 'Creating...' : 'Create'}
        </button>
        <button className="upload-cancel-btn" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function Students({ isTeacher }) {
  const navigate = useNavigate();
  const { classes } = useContext(ClassesContext);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [loginFilter, setLoginFilter] = useState('');
  const [loginSort, setLoginSort] = useState(null); // null | 'desc' | 'asc'
  const [copyStatus, setCopyStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/students`)
      .then((res) => { if (Array.isArray(res.data)) setStudents(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const classesForStudent = (studentId) => classes.filter((c) => (c.students || []).some((s) => s.id === studentId));

  const handleCreated = (newStudent) => {
    setStudents((prev) => [...prev, newStudent]);
    setCreating(false);
  };

  const handleDeleteStudent = async (student) => {
    const confirmed = window.confirm(
      `This will permanently delete ${student.name} and all their data — their app login, every session transcript, checklist and indicator result, and all class enrollments. This cannot be undone.\n\nDelete this student?`
    );
    if (!confirmed) return;
    setDeletingId(student.id);
    try {
      await deleteStudent(student.id, getCurrentUserEmail());
      setStudents((prev) => prev.filter((s) => s.id !== student.id));
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete this student. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const allRealStudents = students.filter((s) => s.tag !== 'demo');
  const allDemoStudents = students.filter((s) => s.tag === 'demo');
  const atStudentCapacity = allRealStudents.length >= MAX_STUDENT_ACCOUNTS;

  const normalizedQuery = searchQuery.trim().toLowerCase();
  // Class filter matches by class id against ClassesContext rosters, not the
  // comma-joined `student.class` string, so class names containing commas
  // can't cause false matches.
  const now = Date.now();
  const loginTest = (LAST_LOGIN_FILTERS.find((f) => f.value === loginFilter) || LAST_LOGIN_FILTERS[0]).test;
  const matchesQuery = (student) =>
    (!classFilter || classesForStudent(student.id).some((c) => c.id === classFilter)) &&
    loginTest(lastLoginMs(student), now) &&
    (!normalizedQuery ||
      student.name.toLowerCase().includes(normalizedQuery) ||
      (student.email || '').toLowerCase().includes(normalizedQuery) ||
      (student.class || '').toLowerCase().includes(normalizedQuery));
  const isFiltering = Boolean(normalizedQuery || classFilter || loginFilter);
  const clearFilters = () => { setSearchQuery(''); setClassFilter(''); setLoginFilter(''); };

  // Never-logged-in students always sort to the bottom, in either direction.
  const sortByLogin = (list) => {
    if (!loginSort) return list;
    return [...list].sort((a, b) => {
      const am = lastLoginMs(a);
      const bm = lastLoginMs(b);
      if (am === null && bm === null) return 0;
      if (am === null) return 1;
      if (bm === null) return -1;
      return loginSort === 'desc' ? bm - am : am - bm;
    });
  };
  // Header click cycles: newest first → oldest first → original order.
  const cycleLoginSort = () => setLoginSort((s) => (s === null ? 'desc' : s === 'desc' ? 'asc' : null));

  const realStudents = sortByLogin(allRealStudents.filter(matchesQuery));
  const demoStudents = sortByLogin(allDemoStudents.filter(matchesQuery));
  const showDemoSection = allDemoStudents.length > 0;

  // Copies the emails of the real (non-demo) students currently shown, so an
  // admin can paste them straight into an email's To/BCC field — e.g. filter
  // to a class + Last login "Never" to send a sign-in reminder.
  const visibleEmails = [...new Set(realStudents.map((s) => (s.email || '').trim()).filter(Boolean))];
  const emailCountLabel = `${visibleEmails.length} email${visibleEmails.length === 1 ? '' : 's'}`;
  const handleCopyEmails = async () => {
    try {
      await navigator.clipboard.writeText(visibleEmails.join(', '));
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
    setTimeout(() => setCopyStatus(''), 2500);
  };

  const renderTableHeader = () => (
    <div className="students-table-header">
      <span className="students-col-name">Name</span>
      <span className="students-col-email">Email</span>
      <span
        className="students-col-login"
        role="columnheader"
        aria-sort={loginSort === 'desc' ? 'descending' : loginSort === 'asc' ? 'ascending' : 'none'}
      >
        <button
          type="button"
          className={`students-sort-button${loginSort ? ' students-sort-button-active' : ''}`}
          onClick={cycleLoginSort}
          title={loginSort === 'desc' ? 'Sorted newest first' : loginSort === 'asc' ? 'Sorted oldest first' : 'Sort by last login'}
        >
          Last Login
          <span className="students-sort-icon" aria-hidden="true">
            {loginSort === 'desc' ? '↓' : loginSort === 'asc' ? '↑' : '↕'}
          </span>
        </button>
      </span>
      <span className="students-col-class">Class</span>
      <span className="students-col-actions"></span>
    </div>
  );

  const renderStudentRow = (student) => {
    const studentClasses = classesForStudent(student.id);
    return (
      <div
        key={student.id}
        className="student-row student-row-clickable"
        onClick={() => navigate(`/admin/students/${student.id}`)}
      >
        <div className="student-avatar-name">
          <div className="student-avatar">{student.name.charAt(0)}</div>
          <span className="student-name">{student.name}</span>
        </div>
        <span className="students-col-email">{student.email}</span>
        <span className="students-col-login">
          {(student.hasLoggedIn && formatLastLogin(student.lastLoginAt))
            || <span className="student-class-empty">Never logged in</span>}
        </span>
        <span className="students-col-class">
          {studentClasses.length > 0
            ? studentClasses.map((c) => (
              <span key={c.id} className="student-class-chip">{c.name}</span>
            ))
            : <span className="student-class-empty">—</span>
          }
        </span>
        <div className="students-col-actions">
          <span className="student-view-link">View →</span>
          {!isTeacher && (
            <button
              className="class-remove-btn"
              onClick={(e) => { e.stopPropagation(); handleDeleteStudent(student); }}
              disabled={deletingId === student.id}
              title="Delete student"
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          <div className="title-name-container">
            <header className="name-header">Students</header>
          </div>
        </div>
        <div className="reload-button-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {!loading && (
            <span className="account-count-indicator">
              {allRealStudents.length}/{MAX_STUDENT_ACCOUNTS} accounts
            </span>
          )}
          <button
            className="reload-button"
            onClick={() => setCreating(true)}
            disabled={creating || atStudentCapacity}
            title={atStudentCapacity ? `Maximum of ${MAX_STUDENT_ACCOUNTS} student accounts reached` : undefined}
          >
            + Add Student
          </button>
        </div>
      </div>

      <div className="component-container">
        <div className="students-content">
          {!loading && (
            <div className="students-search-row">
              <input
                type="text"
                className="students-search-input"
                placeholder="Search by name, email or class..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FilterSelect
                label="Class"
                value={classFilter}
                onChange={setClassFilter}
                options={[
                  { value: '', label: 'All' },
                  ...[...classes].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
              <FilterSelect
                label="Last login"
                value={loginFilter}
                onChange={setLoginFilter}
                options={LAST_LOGIN_FILTERS}
              />
              {isFiltering && (
                <>
                  <span className="students-search-count">
                    {/* Real students only, matching the list above the demo section */}
                    {realStudents.length} of {allRealStudents.length}
                  </span>
                  <button type="button" className="students-clear-filters" onClick={clearFilters}>
                    Clear filters
                  </button>
                </>
              )}
              <button
                type="button"
                className={`students-copy-button${copyStatus === 'copied' ? ' students-copy-button-done' : ''}`}
                onClick={handleCopyEmails}
                disabled={visibleEmails.length === 0}
                title="Copy the emails of the students shown below, comma-separated (excludes demo accounts)"
              >
                {copyStatus === 'copied' ? `✓ Copied ${emailCountLabel}`
                  : copyStatus === 'failed' ? 'Copy failed — check clipboard permissions'
                  : `Copy ${emailCountLabel}`}
              </button>
            </div>
          )}

          {!loading && atStudentCapacity && (
            <p className="chat-card-hint">
              Maximum of {MAX_STUDENT_ACCOUNTS} student accounts reached — delete one to add another.
            </p>
          )}

          {loading ? (
            <div className="cases-loading">Loading students...</div>
          ) : (
            <>
              <div className="students-list">
                {renderTableHeader()}
                {creating && (
                  <CreateStudentRow onCancel={() => setCreating(false)} onCreated={handleCreated} />
                )}
                {realStudents.length === 0 && !creating && (
                  <div className="student-row">
                    <span className="student-class-empty">
                      {isFiltering ? 'No students match your filters.' : 'No students yet.'}
                    </span>
                  </div>
                )}
                {realStudents.map(renderStudentRow)}
              </div>

              {showDemoSection && (
                <>
                  <h2 className="cases-section-heading">
                    Demo Accounts <span className="cases-section-hint">{allDemoStudents.length} accounts</span>
                  </h2>
                  <div className="students-list">
                    {renderTableHeader()}
                    {demoStudents.length === 0 && (
                      <div className="student-row">
                        <span className="student-class-empty">No demo accounts match your filters.</span>
                      </div>
                    )}
                    {demoStudents.map(renderStudentRow)}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Students;
